import { useState, useEffect } from "react";

import {
  MotifProgressLoader,
  MotifTruncate,
  MotifIcon,
  MotifTooltip,
} from "@ey-xd/motif-react";
import {
  IconoirNavArrowUp,
  IconoirNavArrowDown,
  IconoirXmarkCircle,
  IconoirCheckCircle,
  IconoirRefresh,
} from "@ey-xd/motif-icon";
import {
  getFormattedDate,
  getTotalOrderAmount,
  onViewOrderItems,
  getArrowDirection,
  getSyncFormattedDate,
} from "../helper";
import orderEmptyImg from "../../../assets/images/empty_order.svg";
import OrdersTable from "../OrderTable/OrderTable";
import "./MyOrders.scss";
import EmptyOrder from "../../EmptyOrder";
import NoSearchResult from "../../NoSearchResult/NoSearchResult";
import productListEmptyImg from "../../../assets/images/empty_product_list_default.svg";
import ErrorOrderPage from "../../ErrorOrderPage/ErrorOrderPage";
import { containsEmoji } from "../../../utils/common";

interface MyOrdersProps {
  activeOrders: any[];
  isLoadingActive: boolean;
  searchText?: string;
  isActiveError: boolean;
  refreshCallback: () => void;
  handleSyncOrder?: (orderId: number, status?: string) => void;
}

const MyOrders: React.FC<MyOrdersProps> = ({
  activeOrders,
  isLoadingActive,
  searchText,
  isActiveError,
  refreshCallback,
  handleSyncOrder,
}) => {
  const [activeOrderDetails, setActiveOrderDetails] = useState<
    {
      orderParentId: string;
      isExpanded: boolean;
    }[]
  >([]);

  useEffect(() => {
    if (activeOrders.length > 0) {
      const newOrderDetails = activeOrders.map((order: any) => ({
        orderParentId: order.OrderParentId,
        isExpanded: false,
      }));
      setActiveOrderDetails(newOrderDetails);
    }
  }, [activeOrders]);

  const activeOrderColumns: any = [
    {
      key: "OrderName",
      label: "Order",
      width: "33%",
      render: (order: any) => (
        <div className="sku-name-responsive">
          <MotifTruncate maxLines={1} className="sku-truncate-responsive">
            {order.Description}
          </MotifTruncate>
          <div className="order-id">{order.OrderParentId}</div>
        </div>
      ),
    },
    {
      key: "CreatedDtm",
      label: "Date Created",
      width: "17%",
      render: (order: any) => (
        <div className="order-date">{getFormattedDate(order.CreatedDtm)}</div>
      ),
    },
    {
      key: "opportunityCode",
      label: "Opportunity",
      width: "16%",
      render: (order: any) => (
        <div className="order-o-code">
          {order.OpportunityId ? (
            <>
              <div className="order-o-code-items">
                <div>{order.OpportunityId}</div>
                {order.IsCRMSyncRequired && (
                <div>
                  {order.CRMSyncFailureStatus != null &&
                    order.CRMSyncFailureStatus == 0 && (
                      <MotifTooltip
                        placement="auto"
                        allowHover
                        allowClick={false}
                        hideCloseButton
                        trigger={
                          <MotifIcon
                            iconFunction={IconoirCheckCircle}
                            size="15"
                            className="success-icon"
                          />
                        }
                      >
                        <div className="crm-sync-tooltip">
                          <div className="crm-sync-title">
                            Last saved to CRM
                          </div>
                          <div className="crm-sync-body">
                          {getSyncFormattedDate(order.CRMSyncDtm || "")} (UTC)
                          </div>
                        </div>
                      </MotifTooltip>
                    )}
                  {order.CRMSyncFailureStatus != null &&
                    order.CRMSyncFailureStatus >= 1 && (
                      <MotifTooltip
                        placement="auto"
                        allowHover
                        allowClick={false}
                        hideCloseButton
                        trigger={
                          <MotifIcon
                            iconFunction={IconoirXmarkCircle}
                            size="15"
                            className="warning-icon"
                          />
                        }
                      >
                        <div className="crm-sync-tooltip">
                          <div className="crm-sync-title">CRM save failed</div>
                          {order.CRMSyncDtm && (
                            <div className="crm-sync-body">
                              Last successful saved on <br />
                            {getSyncFormattedDate(order.CRMSyncDtm || "")} (UTC)
                            </div>
                          )}
                        </div>
                      </MotifTooltip>
                    )}
                </div>
                )}
              </div>
              {order.IsCRMSyncRequired && (
              <div className="retry-sync-container">
                {order.IsCRMSyncRetryAllowed == 1 && handleSyncOrder && (
                  <div
                    className="retry-sync"
                    onClick={() =>
                      handleSyncOrder(order.OrderParentId, "active")
                    }
                  >
                    <div>Retry Sync</div>
                    <div>
                      <MotifIcon
                        iconFunction={IconoirRefresh}
                        size="15"
                        className="retry-icon"
                      />
                    </div>
                  </div>
                )}
              </div>
              )}
            </>
          ) : (
            "----"
          )}
        </div>
      ),
    },
    {
      key: "Total",
      label: "Order Cost",
      width: "16%",
      render: (order: any) => (
        <div className="order-cost">{getTotalOrderAmount(order)}</div>
      ),
    },

    {
      key: "View",
      label: "View Order Items",
      width: "17%",
      render: (order: any) => {
        const isExpanded =
          getArrowDirection(order.OrderParentId, activeOrderDetails) === "up";
        return (
          <div className="view-order">
            <div
              className="view-order-items"
              onClick={() =>
                setActiveOrderDetails(
                  onViewOrderItems(order.OrderParentId, activeOrderDetails),
                )
              }
            >
              <div>{isExpanded ? "Close Order Items" : "View Order Items"}</div>
              <div>
                {isExpanded ? (
                  <MotifIcon iconFunction={IconoirNavArrowUp} size="20" />
                ) : (
                  <MotifIcon iconFunction={IconoirNavArrowDown} size="20" />
                )}
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  let content;

  if (isLoadingActive) {
    return <MotifProgressLoader show variant="default" />;
  }

  if (!activeOrders.length) {
    if (isActiveError) {
      content = (
        <ErrorOrderPage
          image={productListEmptyImg}
          header="An error has occurred while attempting to retrieve data. Please choose the option to refresh the page."
          handleRefresh={refreshCallback}
        />
      );
    } else {
      const isContainEmoji = containsEmoji(searchText || "");
      if (searchText && !isContainEmoji) {
        content = <NoSearchResult searchValue={searchText} />;
      } else {
        content = (
          <EmptyOrder
            image={orderEmptyImg}
            header="No orders are placed yet"
            subHeader="It looks like you do not have any orders yet. Please browse our technology products to start adding items to your order bundles."
          />
        );
      }
    }
  } else {
    content = (
      <OrdersTable
        data={activeOrders}
        orderDetails={activeOrderDetails}
        columnsData={activeOrderColumns}
        showEngagementColumn={true}
      />
    );
  }
  return (
    <div className="workspace-orders-section">
      <div className="workspace-orders-list">
        <div className="workspace-orders-header">
          <h3 className="workspace-orders-title">My Orders</h3>
          <p className="workspace-orders-description">
            Products and services you're currently using across your engagements
          </p>
        </div>
        <div className="workspace-orders-content">{content}</div>
      </div>
    </div>
  );
};
export default MyOrders;
